import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import ParticleCanvas from '../components/common/ParticleCanvas';
import { Lock, Mail, ShieldCheck, AlertCircle, Key, ArrowLeft, CheckCircle2 } from 'lucide-react';

const Login = ({ onSwitchToRegister, onGoToHome }) => {
  const { login, sendForgotPasswordOTP, resetPassword, authError, setAuthError } = useAuth();
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Forgot Password Mode & Steps
  // mode: 'login' | 'forgot_email' | 'forgot_otp'
  const [mode, setMode] = useState('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setIsSubmitting(true);
    setAuthError('');
    setSuccessMsg('');
    await login(email, password);
    setIsSubmitting(false);
  };

  const handleSendResetOTP = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setIsSubmitting(true);
    setAuthError('');
    setSuccessMsg('');

    const result = await sendForgotPasswordOTP(forgotEmail);
    setIsSubmitting(false);

    if (result.success) {
      setMode('forgot_otp');
      setSuccessMsg(result.message || 'Password reset OTP code sent to your email address.');
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!resetOtp || !newPassword) return;
    if (newPassword !== confirmPassword) {
      setAuthError('New password and confirm password do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setAuthError('New password must be at least 6 characters long.');
      return;
    }

    setIsSubmitting(true);
    setAuthError('');

    const result = await resetPassword(forgotEmail, resetOtp, newPassword);
    setIsSubmitting(false);

    if (result.success) {
      setSuccessMsg('Password reset successfully! Please sign in with your new password.');
      setMode('login');
      setEmail(forgotEmail);
      setPassword('');
      setForgotEmail('');
      setResetOtp('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  return (
    <div className="relative min-h-screen bg-[#09090B] flex items-center justify-center p-4 font-sans overflow-hidden">
      {/* Interactive Infinite Canvas Background */}
      <ParticleCanvas />

      <div className="relative z-10 w-full max-w-md bg-[#121215]/90 backdrop-blur-xl border border-[#27272A] rounded-3xl p-8 shadow-card">
        <div className="text-center mb-8">
          {/* <img 
            src="/logo.png" 
            alt="DUBAI GOLD X Logo" 
            className="w-16 h-16 object-contain mx-auto mb-3 drop-shadow-[0_0_25px_rgba(245,158,11,0.45)]" 
          /> */}
          <h2 className="text-2xl font-extrabold tracking-tight text-[#FFFFFF]">
            {mode === 'login' ? 'Sign In to Platform' : mode === 'forgot_email' ? 'Forgot Password' : 'Reset Password'}
          </h2>
          <p className="text-xs text-[#9CA3AF] mt-1">
            {mode === 'login' 
              ? 'DUBAI GOLD X MT5 Copy Trading & Profit Sharing' 
              : mode === 'forgot_email'
              ? 'Enter your registered email to receive a password reset code'
              : `Enter the 6-digit OTP sent to ${forgotEmail}`}
          </p>
        </div>

        {authError && (
          <div className="mb-6 p-4 bg-[#EF4444]/15 border border-[#EF4444]/40 rounded-2xl flex items-start space-x-3 text-xs text-[#EF4444] font-medium">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{authError}</span>
          </div>
        )}

        {successMsg && (
          <div className="mb-6 p-4 bg-[#10B981]/15 border border-[#10B981]/40 rounded-2xl flex items-start space-x-3 text-xs text-[#10B981] font-medium">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* 1. STANDARD LOGIN FORM */}
        {mode === 'login' && (
          <form onSubmit={handleLoginSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setAuthError(''); setSuccessMsg(''); }}
                  placeholder="user@dubaigoldx.com"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316] transition-colors"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-[#9CA3AF]">Password</label>
                <button
                  type="button"
                  onClick={() => { setMode('forgot_email'); setAuthError(''); setSuccessMsg(''); setForgotEmail(email); }}
                  className="text-xs text-[#F97316] font-semibold hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setAuthError(''); setSuccessMsg(''); }}
                  placeholder="••••••••"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316] transition-colors"
                />
              </div>
            </div>

            <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Authenticating...' : 'Sign In Securely'}
            </Button>
          </form>
        )}

        {/* 2. FORGOT PASSWORD - STEP 1 (ENTER EMAIL) */}
        {mode === 'forgot_email' && (
          <form onSubmit={handleSendResetOTP} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Registered Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                <input
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => { setForgotEmail(e.target.value); setAuthError(''); }}
                  placeholder="Enter your registered email..."
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316] transition-colors"
                />
              </div>
            </div>

            <Button type="submit" variant="primary" size="lg" className="w-full mt-2" disabled={isSubmitting}>
              {isSubmitting ? 'Sending Reset Code...' : 'Send Password Reset Code'}
            </Button>

            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={() => { setMode('login'); setAuthError(''); }}
                className="text-xs text-[#9CA3AF] hover:text-white inline-flex items-center space-x-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Back to Sign In</span>
              </button>
            </div>
          </form>
        )}

        {/* 3. FORGOT PASSWORD - STEP 2 (ENTER OTP & NEW PASSWORD) */}
        {mode === 'forgot_otp' && (
          <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">6-Digit Reset Code (OTP)</label>
              <div className="relative">
                <Key className="w-4 h-4 text-[#F59E0B] absolute left-3.5 top-3.5" />
                <input
                  type="text"
                  required
                  maxLength={6}
                  value={resetOtp}
                  onChange={(e) => { setResetOtp(e.target.value); setAuthError(''); }}
                  placeholder="123456"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-sm font-mono tracking-widest text-center font-bold focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316] transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">New Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setAuthError(''); }}
                  placeholder="At least 6 characters"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316] transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Confirm New Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setAuthError(''); }}
                  placeholder="Confirm new password"
                  className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316] transition-colors"
                />
              </div>
            </div>

            <Button type="submit" variant="primary" size="lg" className="w-full mt-2" disabled={isSubmitting}>
              {isSubmitting ? 'Resetting Password...' : 'Reset Password & Sign In'}
            </Button>

            <div className="flex items-center justify-between text-xs pt-2">
              <button
                type="button"
                onClick={() => { setMode('forgot_email'); setAuthError(''); }}
                className="text-[#9CA3AF] hover:text-[#FFFFFF] flex items-center space-x-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Change Email</span>
              </button>

              <button
                type="button"
                onClick={handleSendResetOTP}
                className="text-[#F97316] font-bold hover:underline"
              >
                Resend Code
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 pt-6 border-t border-[#27272A] text-center space-y-2">
          <p className="text-xs text-[#9CA3AF]">
            Don't have an account?{' '}
            <button
              onClick={onSwitchToRegister}
              className="text-[#F97316] font-bold hover:underline hover:text-[#FB923C]"
            >
              Register here
            </button>
          </p>

          {onGoToHome && (
            <button
              onClick={onGoToHome}
              className="text-xs text-[#9CA3AF] hover:text-white font-semibold flex items-center justify-center space-x-1 mx-auto pt-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Home</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
